<template>
  <div class="w-full bg-zt-white rounded-zt-md p-6" :class="[boxLogin.styles.shadow]" :style="[boxLogin.styles.box]">
    <router-link v-if="!isChangePasswordSuccess" :to="{ name: 'login' }">
      <span class="flex items-center text-zt-dark-400" :style="[boxLogin.styles.form.textColor]">
        <zt-icon name="angle-left" class="mr-2" /> {{ $t('message.loginComponent.backToPreview') }}
      </span>
    </router-link>
    <div class="flex flex-col items-center">
      <div
        class="mb-8 mt-3 flex justify-center"
        :class="[boxLogin.sizeLogo === 'full' ? 'w-full' : '']"
        :style="boxLogin.sizeLogo !== 'full' ? 'width: 196px' : ''"
      >
        <img class="format-img" :src="boxLogin.logoUrl" alt="" />
      </div>
      <div class="flex flex-col items-center w-full" v-if="!isChangePasswordSuccess">
        <graphic-base width="100" height="100" view-box="0 0 126 126">
          <circle-arrow-lock />
        </graphic-base>
        <div class="text-center w-11/12 mt-10 text-zt-primary-200" :style="[boxLogin.styles.form.textColor]">
          <h1 class="text-zt-lg mb-3 font-bold">
            {{ $t('message.resetPassComponent.title') }}
          </h1>
        </div>
      </div>
      <div class="w-full" v-if="forgotPassErrors.showMessage">
        <zt-alert :text="forgotPassErrors.message" color="danger" :duration="forgotPassErrors.MAX_DURATION_ALERT"
          @afterClosed="forgotPassErrors.showMessage = false" />
      </div>
      <div class="mt-4 w-full">
        <ValidationObserver v-slot="{ invalid }">
          <zt-auth-forgot-pass-fields v-if="!isChangePasswordSuccess" v-model="forgotPassFields"
            :login-custom-params-fields="boxLogin" @activeButton="fieldActiveButton(invalid)" />
          <zt-auth-forgot-pass-success v-if="isChangePasswordSuccess" :first-login-custom-params-fields="boxLogin"
            :resend-params="forgotPassFields" />
          <div class="mb-4" v-if="!isChangePasswordSuccess">
            <zt-button class="w-full opacity-100" :style="[bgButtonColor(invalid)]"
              :disabled="invalid || disabledButton" @click="submitForgotPass">
              <span v-if="!isLoadingForgotPass">
                {{ $t('message.loginComponent.forgetPassword.send') }}
              </span>
              <vue-loading v-if="isLoadingForgotPass" background-color="rgba(0,0,0,0.0)" :active="isLoadingForgotPass"
                color="#4F41A3" :width="30" :height="30" :is-full-page="false" />
            </zt-button>
          </div>
        </ValidationObserver>
      </div>
      <div class="flex flex-col items-center">
        <a @click="activeForgotPasswordModal" class="text-zt-dark-300 flex items-center justify-center" :style="[
          boxLogin.styles.form.textColor
        ]" data-vs-link="auth-forgotpass-link-activemodal">
          <p>{{ $t('message.loginComponent.login.help') }}</p>
        </a>
      </div>

      <zt-auth-forgot-password-help-dialog :is-active-modal-forgot-password="isActiveModalForgotPassword" />
    </div>
  </div>
</template>

<script>
import loginCustomParams from '@/modules/Auth/mixins/loginCustomParams'
import Users from '@/apiClient/Users'
import { mapGetters } from 'vuex'
import GraphicBase from '@/components/commons/Illustrations/graphicBase'
import CircleArrowLock from '@/components/commons/Illustrations/graphicSVG/circleArrowLock'
import ZtAuthForgotPassFields from '@/modules/Auth/components/authForgotPassFields'
import ZtAuthForgotPassSuccess from '@/modules/Auth/components/authForgotPassSuccess'
import ztAuthForgotPasswordHelpDialog from '@/modules/Auth/components/authForgotPasswordHelpDialog'
import { store, mutation } from '@/modules/Auth/hooks/activeForgotPasswordModal'

export default {
  name: 'ztForgotPassword',
  components: {
    ZtAuthForgotPassSuccess,
    ZtAuthForgotPassFields,
    CircleArrowLock,
    GraphicBase,
    ztAuthForgotPasswordHelpDialog
  },
  mixins: [loginCustomParams],
  provide: { store, mutation },
  data() {
    return {
      objectLoginParams: {},
      forgotPassFields: {
        userName: '',
        userEmail: ''
      },
      disabledButton: false,
      isLoadingForgotPass: false,
      forgotPassErrors: {
        message: '',
        showMessage: '',
        MAX_DURATION_ALERT: 4
      },
      isChangePasswordSuccess: false
    }
  },
  computed: {
    ...mapGetters({ getLoginCustomParams: 'auth/getLoginCustomParams' }),
    bgButtonColor() {
      return invalid =>
        invalid
          ? { backgroundColor: '#E2E2E2' }
          : this.boxLogin.styles.form.buttons.bgColor
    },
    isActiveModalForgotPassword() {
      return store.activeForgotPasswordModal
    },
    performanceToken() {
      return this.$iframeMessages.getTokenPerformanceValue()
    }
  },
  methods: {
    async submitForgotPass() {
      try {
        await this.$recaptchaLoaded()
        await this.$recaptcha('login')
        this.isLoadingForgotPass = true
        this.disabledButton = true
        await this.user
          .resetPassRequest(
            this.forgotPassFields.userName.toLowerCase().trim(),
            this.forgotPassFields.userEmail,
            this.performanceToken
          )
          .then(response => {
            if (response.status === 200) {
              this.isLoadingForgotPass = false
              this.disabledButton = false
              this.isChangePasswordSuccess = true
            }
          })
          .catch(error => {
            this.handleErrors(error)
          })
      } catch (error) {
        console.error('Error: ', error)
        this.handleErrors(error)
      }
    },
    fieldActiveButton(invalid) {
      if (!invalid) this.submitForgotPass()
    },
    handleErrors(error) {
      try {
        if (
          error.hasOwnProperty('response') &&
          error.response &&
          error.response.data &&
          error.response.data.code === '00151'
        ) {
          this.isLoadingForgotPass = false
          this.disabledButton = false
          this.forgotPassErrors.showMessage = true
          this.forgotPassErrors.message = error.response.data.message || this.$t(
            `message.errorReference.${error.response.data.code}`
          )
          return
        }
        this.isLoadingForgotPass = false
        this.disabledButton = false
        this.forgotPassErrors.showMessage = true
        typeof error === 'string'
          ? (this.forgotPassErrors.message = this.$t(
            `message.errorReference.${error}`
          ))
          : (this.forgotPassErrors.message = this.$t(
            `message.loginComponent.forgetPassword.err400ForgotPass`
          ))
      } catch (e) {
        this.forgotPassErrors.showMessage = true
        this.forgotPassErrors.message = this.$t(`message.errorReference.00003`)
        this.isLoadingForgotPass = false
        this.disabledButton = false
      }
    },
    activeForgotPasswordModal() {
      mutation.setActiveForgotPasswordModal(true)
    }
  },
  created() {
    this.user = new Users()
    this.objectLoginParams = this.getLoginCustomParams.loginCustom
  }
}
</script>

<style scoped lang="scss">
.format-img {
  max-height: 120px;
  max-width: 100%;
  vertical-align: middle;
  width: auto;
  object-fit: contain;
  -ms-interpolation-mode: bicubic;
  display: inline-block;
}

::v-deep .zt-button {
  height: 3.25rem;
}
</style>
